import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useCart } from '../context/CartContext';
import { useLocalized } from '../hooks/useLocalized';
import { getEffectivePrice, isDiscounted, discountPercent } from '../utils/pricing';

const DrawerOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 9999;
  opacity: ${props => props.isOpen ? '1' : '0'};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const DrawerContainer = styled.div<{ isOpen: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  z-index: 10000;
  transform: translateY(${props => props.isOpen ? '0' : '100%'});
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: transform 0.4s cubic-bezier(0.23, 1, 0.32, 1), visibility 0.4s ease;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
`;

const DrawerHeader = styled.div`
  padding: 20px 24px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DrawerTitle = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: 16px;
  font-weight: 500;
  color: #000;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  opacity: 0.6;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 1;
  }

  svg {
    width: 20px;
    height: 20px;
    stroke: #000;
    stroke-width: 1;
  }
`;

const DrawerContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;
`;

const CartItem = styled.div`
  display: flex;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

const ItemImage = styled.img`
  width: 100px;
  height: 100px;
  object-fit: contain;
  flex-shrink: 0;
  border-radius: 4px;
`;

const ItemDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ItemName = styled.h3`
  font-family: 'Cormorant Garamond', serif;
  font-size: 14px;
  font-weight: 400;
  color: #000;
  margin: 0;
  line-height: 1.3;
`;

const ItemInfo = styled.div`
  font-size: 12px;
  color: #666;
  font-weight: 300;
`;

const ItemPrice = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  color: #000;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
`;

const QuantityButton = styled.button`
  width: 28px;
  height: 28px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
  border-radius: 4px;

  &:hover {
    border-color: #000;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityDisplay = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  min-width: 24px;
  text-align: center;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  margin-top: 8px;

  &:hover {
    color: #000;
  }
`;

const EmptyCart = styled.div`
  padding: 60px 24px;
  text-align: center;
  color: #666;
  font-size: 14px;
`;

const DrawerFooter = styled.div`
  padding: 20px 24px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: #f7f3eb;
`;

const CartTotal = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
`;

const TotalLabel = styled.span`
  font-family: 'Cormorant Garamond', serif;
  font-size: 14px;
  font-weight: 500;
  color: #000;
`;

const TotalAmount = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 400;
  color: #000;
`;

const CheckoutButton = styled(Link)`
  display: block;
  width: 100%;
  padding: 16px 24px;
  background: #000;
  color: white;
  border: none;
  font-family: 'Cormorant Garamond', serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 12px;
  border-radius: 4px;
  text-decoration: none;
  text-align: center;

  &:hover {
    background: #333;
  }
`;

const ViewCartButton = styled(Link)`
  display: block;
  width: 100%;
  padding: 16px 24px;
  background: transparent;
  color: #000;
  border: 1px solid #000;
  font-family: 'Cormorant Garamond', serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  text-decoration: none;
  border-radius: 4px;

  &:hover {
    background: #000;
    color: white;
  }
`;

interface MobileCartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileCartDrawer: React.FC<MobileCartDrawerProps> = ({ isOpen, onClose }) => {
  const { state: cartState, updateQuantity: updateCartQuantity, removeItem: removeCartItem } = useCart();
  const { getProductName, getLocalized } = useLocalized();

  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [isOpen, onClose]);

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeCartItem(productId);
    } else {
      updateCartQuantity(productId, quantity);
    }
  };

  const removeItem = (productId: string) => {
    removeCartItem(productId);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price).replace('₺', '') + ' TL';
  };

  return (
    <>
      <DrawerOverlay isOpen={isOpen} onClick={onClose} />
      <DrawerContainer isOpen={isOpen}>
        <DrawerHeader>
          <DrawerTitle>{getLocalized('Alışveriş sepetiniz', 'Your cart')} ({cartState.itemCount})</DrawerTitle>
          <CloseButton onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </CloseButton>
        </DrawerHeader>

        <DrawerContent>
          {cartState.items.length === 0 ? (
            <EmptyCart>
              {getLocalized('Alışveriş sepetiniz boş', 'Your cart is empty')}
            </EmptyCart>
          ) : (
            cartState.items.map((item) => {
              const hasAccessories = item.accessories && item.accessories.length > 0;

              // Calculate item total including accessories (uses discounted price when set)
              let itemTotal = getEffectivePrice(item.product) * item.quantity;
              if (hasAccessories) {
                const accessoriesTotal = item.accessories!.reduce((sum, acc) => sum + acc.price, 0);
                itemTotal += accessoriesTotal * item.quantity;
              }

              // Get color-specific image if available
              const colorImages = item.selectedColorIndex !== undefined
                ? item.product.colors?.[item.selectedColorIndex]?.images
                : undefined;
              const displayImage = colorImages && colorImages.length > 0
                ? colorImages[0].url
                : item.product.images[0]?.url || '';

              // Get color name if selected
              const selectedColorName = item.selectedColorIndex !== undefined
                ? item.product.colors?.[item.selectedColorIndex]?.name
                : undefined;

              return (
              <CartItem key={item.cartItemId}>
                <ItemImage src={displayImage} alt={getProductName(item.product)} />
                <ItemDetails>
                  <ItemName>{getProductName(item.product)}</ItemName>
                  <ItemInfo>
                    {selectedColorName && (
                      <>{getLocalized('Renk:', 'Color:')} {selectedColorName}<br /></>
                    )}
                    {hasAccessories && (
                      <>
                        <strong>{getLocalized('Dahil edilen aksesuarlar:', 'Included accessories:')}</strong><br />
                        {item.accessories!.map((acc, idx: number) => (
                          <span key={idx}>
                            • {acc.name} <span style={{ fontFamily: "'Inter', sans-serif" }}>(+₺{acc.price.toLocaleString()})</span><br />
                          </span>
                        ))}
                      </>
                    )}
                    {getLocalized('Miktar:', 'Quantity:')} <span style={{ fontFamily: "'Inter', sans-serif" }}>{item.quantity}</span>
                  </ItemInfo>
                  <ItemPrice>
                    {isDiscounted(item.product) ? (
                      <>
                        <span style={{ textDecoration: 'line-through', color: '#b0b0b0', marginRight: 6, fontSize: '0.9em' }}>
                          {formatPrice(itemTotal + (item.product.price - getEffectivePrice(item.product)) * item.quantity)}
                        </span>
                        {formatPrice(itemTotal)}
                        <span style={{ color: '#8a1a1a', fontSize: '0.85em', marginLeft: 6 }}>-%{discountPercent(item.product)}</span>
                      </>
                    ) : formatPrice(itemTotal)}
                  </ItemPrice>
                  <QuantityControls>
                    <QuantityButton
                      onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                    >
                      −
                    </QuantityButton>
                    <QuantityDisplay>{item.quantity}</QuantityDisplay>
                    <QuantityButton
                      onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                    >
                      +
                    </QuantityButton>
                  </QuantityControls>
                  <RemoveButton onClick={() => removeItem(item.cartItemId)}>
                    {getLocalized('Çıkar', 'Remove')}
                  </RemoveButton>
                </ItemDetails>
              </CartItem>
              );
            })
          )}
        </DrawerContent>

        {cartState.items.length > 0 && (
          <DrawerFooter>
            <CartTotal>
              <TotalLabel>{getLocalized('Ara Toplam', 'Subtotal')}</TotalLabel>
              <TotalAmount>{formatPrice(cartState.total)}</TotalAmount>
            </CartTotal>
            <CheckoutButton to="/cart" onClick={onClose}>
              {getLocalized('ALIŞVERİŞ SEPETİNE GİT', 'VIEW CART')}
            </CheckoutButton>
            <ViewCartButton to="/guest-checkout" onClick={onClose}>
              {getLocalized('ÖDEMEYE GEÇ', 'CHECKOUT')}
            </ViewCartButton>
          </DrawerFooter>
        )}
      </DrawerContainer>
    </>
  );
};

export default MobileCartDrawer;
